// Los renglones de la relación de venta.
//
// Va aparte de la pantalla para poder probarlo sin abrir un navegador: es la
// cuenta que se le enseña al cliente cuando se le va a cobrar, y una cuenta
// que solo se puede comprobar mirándola es una cuenta que nadie comprueba.
//
// Aquí NO se recalcula ninguna plata. El total de cada renglón viene de la
// venta tal como se guardó, igual que en el archivo de papeles: rehacer la
// multiplicación sería una segunda cuenta que puede diferir de la del papel
// que el cliente ya tiene firmado en la mano.

// Aplana las ventas del periodo en un renglón por producto despachado.
//
// Vienen agrupadas por venta porque así se guardan —una venta, varias
// líneas—, pero la hoja se lee por día: el cliente busca «el del 4 de junio»,
// no «la venta 47». Cada renglón se queda con el número de la remisión para
// poder ir del renglón al papel que se firmó.
export function renglonesDeLaCuenta(ventas) {
  // ── UN RENGLÓN POR DESPACHO, NO POR LOTE ─────────────────────────────────
  //
  // «Gabriel quiere que esta factura del queso salga como la factura de la
  // leche.»
  //
  // La de la leche tiene un renglón por DÍA. La del queso tenía uno por LOTE:
  // el 22 de agosto salían dieciséis renglones de la misma remisión, porque
  // esos 1.621 kilos se sacaron de trece lotes distintos de la cámara.
  //
  // De cuál lote salió cada kilo es cosa de la quesera, no del cliente. Él
  // recibió UN camión con 1.621 kilos de campesino. Y el lote sigue impreso
  // donde importa: en la remisión que firmó al descargar, que es el papel con
  // el que se rastrea un queso malo.
  //
  // Se agrupa por remisión, producto y PRECIO. El precio va en la llave para
  // que la columna VALOR siga siendo cierta: si un día se despacharon dos
  // precios del mismo queso, son dos renglones y no un promedio inventado.
  const grupos = new Map();
  for (const v of ventas || []) {
    for (const l of v.lineas || []) {
      // El queso va por kilos y la leche cruda por litros. `kilos` en cero con
      // `piezas` lleno es cómo se guarda lo que no se pesa; tomar solo `kilos`
      // imprimiría una cantidad de cero al lado de un total con plata.
      const cantidad = Number(l.kilos) > 0 ? Number(l.kilos) : Number(l.piezas) || 0;
      const precio = Number(l.precio) || 0;
      const clave = [v.id, l.producto, precio, l.unidad || 'kg'].join('|');

      if (!grupos.has(clave)) {
        grupos.set(clave, {
          fecha: v.fecha,
          venta: v.id,
          producto: l.producto,
          unidad: l.unidad || 'kg',
          lotes: [],
          contado: v.forma_pago !== 'credito',
          cantidad: 0,
          precio,
          total: 0,
        });
      }
      const g = grupos.get(clave);
      g.cantidad = Math.round((g.cantidad + cantidad) * 100) / 100;
      // El total se SUMA de los renglones guardados, no se recalcula
      // multiplicando: cada línea trae el suyo tal como quedó en la venta, y
      // rehacerlo sería una segunda cuenta que puede diferir del papel que el
      // cliente ya tiene firmado.
      g.total += Math.round(Number(l.total) || 0);
      if (l.lote && !g.lotes.includes(l.lote)) g.lotes.push(l.lote);
    }
  }

  const filas = [...grupos.values()].map(g => ({
    ...g,
    // El primero, para el resumen de una línea; la lista entera va debajo.
    lote: g.lotes[0] || null,
    lotes: g.lotes.sort(),
  }));
  // Por día y, dentro del día, por remisión: es el orden en que salieron los
  // camiones y el orden en que el cliente los tiene anotados.
  return filas.sort((a, b) =>
    String(a.fecha).slice(0, 10).localeCompare(String(b.fecha).slice(0, 10))
    || a.venta - b.venta);
}

// Los subtotales, UNA CIFRA POR UNIDAD.
//
// La hoja de HECTOR QUINTERO decía «SUBTOTALES 523,30 kg» sumando 473,30 kilos
// de queso con 50 litros de suero. Es la clase de cifra que no significa nada y
// que además desmiente la propia tabla de arriba: quien la revise con el dedo
// suma los kilos, le dan 473,30, y ya no le cree al papel.
//
// Se devuelven agrupados —«473,30 kg · 50 L»— porque un negocio que vende por
// peso y por medida tiene dos totales, no uno.
export function subtotalesPorUnidad(renglones) {
  const por = new Map();
  for (const l of renglones || []) {
    const u = l.unidad || 'kg';
    por.set(u, (por.get(u) || 0) + (Number(l.cantidad) || 0));
  }
  // El kilo primero, que es el grueso del negocio; lo demás por orden alfabético
  // para que la hoja de un mes se lea igual que la del siguiente.
  return [...por.entries()]
    .sort((a, b) => (a[0] === 'kg' ? -1 : b[0] === 'kg' ? 1 : a[0].localeCompare(b[0])))
    .map(([unidad, cantidad]) => ({ unidad, cantidad }));
}
